import { TagDropdown } from "@dash/form";
import { useTheme } from "@emotion/react";
import React, { useRef, useState } from "react";

import { megaraxRoles } from "@megaron/access-control";

type Props = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
};

const maxSuggestions = 8;

export const RolesField: React.FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [cursorPos, setCursorPos] = useState(0);
  const [isFocused, setIsFocused] = useState(false);

  const updateCursorPos = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    setCursorPos(e.currentTarget.selectionStart);
  };

  const currentLine = (props.value.slice(0, cursorPos).split("\n").pop() ?? "").trim();

  const existingRoles = props.value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");

  const suggestions =
    isFocused && currentLine !== ""
      ? megaraxRoles
          .filter((role) => role.toLowerCase().includes(currentLine.toLowerCase()))
          .filter((role) => role !== currentLine && !existingRoles.includes(role))
          .slice(0, maxSuggestions)
      : [];

  const handleSuggestionClick = (role: string) => {
    const value = props.value;
    const lineStart = value.lastIndexOf("\n", cursorPos - 1) + 1;
    const nextNewlineIndex = value.indexOf("\n", cursorPos);
    const restOfValue = nextNewlineIndex === -1 ? "\n" : value.slice(nextNewlineIndex);

    const newValue = value.slice(0, lineStart) + role + restOfValue;
    const newCursorPos = lineStart + role.length + 1;

    props.onChange(newValue);
    setCursorPos(newCursorPos);

    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
    });
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    setIsFocused(true);
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setIsFocused(false), 150);
  };

  return (
    <div css={{ display: "flex", flexDirection: "column", width: "100%" }} className={props.className}>
      <label
        css={{
          color: theme.colors.primary,
          fontSize: "0.875rem",
          fontFamily: theme.displayFontFamily,
          marginBottom: "0.25rem",
        }}
      >
        Role
      </label>
      <div css={{ position: "relative" }}>
        {suggestions.length > 0 && <TagDropdown filteredTags={suggestions} handleTagClick={handleSuggestionClick} />}
        <textarea
          ref={textareaRef}
          value={props.value}
          onChange={(e) => {
            props.onChange(e.target.value);
            updateCursorPos(e);
          }}
          onKeyUp={updateCursorPos}
          onClick={updateCursorPos}
          onFocus={handleFocus}
          onBlur={handleBlur}
          css={{
            width: "100%",
            minHeight: "400px",
            border: "none",
            padding: "0.5rem 0.75rem",
            fontSize: "1rem",
            borderRadius: "6px",
            backgroundColor: theme.colors.primaryLight,
            ":focus": {
              outline: `1px solid ${theme.colors.primary}`,
              outlineOffset: "-1px",
            },
          }}
        />
      </div>
    </div>
  );
};
